import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import cn from 'classnames';

import { useTheme } from '../../theme';
import { BaseButton } from '../../BaseComponents';
import CdnImage from '../../CdnImage';
import AddBooking, { FormType } from '../../AddBooking';
import { reachGoal, UserGoal } from '../../analytics';
import { useConfig } from '../../context';
import { getCheckInUrl } from '../../utils';
import { findOrderValues } from '../../AddBooking/components/AddBooking';
import { useCheckinInfoQuery, useFindOrderMutation } from '@websky/graphql';
import ModalConfirm from '../../ModalConfirm';

const ACCOUNT_ROUTES = {
	orders: '/account/orders'
};

interface Props {
	className?: string;
	onRefetchOrders?: () => void;
	additionalButtons?: React.ReactElement[];
}

const ProfileControls: React.FC<Props> = ({ className, onRefetchOrders, additionalButtons }) => {
	const { ProfileControls: css } = useTheme('ProfileControls');
	const { t } = useTranslation('Account');
	const history = useHistory();
	const routerLocation = useLocation();

	const {
		Account: { engineURL, openCheckinPageOnProfileControlsClick },
		Engine: { checkInURL },
		global: {
			companyInfo: { iataCode },
			reserveEngines,
			showBookingPreview
		}
	} = useConfig();

	const [order, setOrder] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const [addBookingModalType, setAddBookingModalType] = useState<FormType>(null);
	const lastAddBookingModalType = useRef<FormType>(null);

	// Prevent title flicker when closing the modal: addBookingModalType is set to null
	// immediately, but AddBooking still renders for a frame during the close animation.
	// Without the ref fallback, null !== FormType.Booking would briefly show the wrong title.
	const lastModalTypeRef = useRef<FormType>(null);

	useEffect(() => {
		if (addBookingModalType) {
			lastModalTypeRef.current = addBookingModalType;
		}
	}, [addBookingModalType]);

	const onSetAddBookingModalType = useCallback(
		(type: FormType) => () => {
			if (type === FormType.Checkin && openCheckinPageOnProfileControlsClick) {
				window.open(checkInURL);

				return;
			}

			lastAddBookingModalType.current = type;
			setAddBookingModalType(type);
		},
		[setAddBookingModalType, openCheckinPageOnProfileControlsClick, checkInURL]
	);

	const onClearAddBookingModalType = useCallback(() => {
		if (addBookingModalType === FormType.Booking) {
			setOrder(null);
		}

		setAddBookingModalType(null);
	}, [addBookingModalType, setAddBookingModalType]);

	const [unlockOrder] = useFindOrderMutation();
	const { refetch } = useCheckinInfoQuery({ skip: true });

	const onBookingSubmit = useCallback(async ({ lastName, ticket, saveInProfile = false }: findOrderValues) => {
		setIsLoading(true);
		const needToSaveInProfile = !showBookingPreview || saveInProfile;

		try {
			const { data, errors } = await unlockOrder({
				variables: {
					params: {
						id: ticket,
						secret: lastName,
						saveInProfile: needToSaveInProfile
					}
				}
			});

			const result = !errors && data && data.FindOrder;

			if (result) {
				onRefetchOrders?.();
				reachGoal(UserGoal.BindOrder);
				setIsLoading(false);

				if (!needToSaveInProfile) {
					setOrder(result);
				} else {
					onClearAddBookingModalType();
					goToOrders();
				}

				return true;
			} else {
				setIsLoading(false);
				setOrder(null);

				return { ticket: t('RetrieveBooking:Order not found') };
			}
		} catch (e) {
			return {
				ticket: t(
					'An error occurred while executing the request. Please try again later or contact a customer support service.'
				)
			};
		} finally {
			setIsLoading(false);
		}
	}, []);

	const onCheckinSubmit = useCallback(async ({ lastName, ticket }: findOrderValues) => {
		if (reserveEngines?.redirectToWebsky1 && reserveEngines?.checkin) {
			window.open(getCheckInUrl(checkInURL, '', ticket, lastName, reserveEngines));

			return true;
		} else {
			setIsLoading(true);

			try {
				const { data, errors } = await refetch({
					params: {
						id: ticket,
						secret: lastName
					}
				});
				const result = !errors && data && data.CheckinInfo;

				if (result.isAvailable) {
					onClearAddBookingModalType();

					window.open(getCheckInUrl(checkInURL, '', ticket, lastName));

					return true;
				} else {
					return { ticket: t('RetrieveBooking:Order not found') };
				}
			} catch (e) {
				return {
					ticket: t(
						'An error occurred while executing the request. Please try again later or contact a customer support service.'
					)
				};
			} finally {
				setIsLoading(false);
			}
		}
	}, []);

	const goToOrders = () => {
		history.push(ACCOUNT_ROUTES.orders);
	};

	const onViewOrderClick = () => {
		onRefetchOrders?.();
		onClearAddBookingModalType();

		goToOrders();
	};

	const openSearchFlights = () => window.open(engineURL);

	const onClearOrder = () => {
		setOrder(null);
	};

	const isOrdersPage = routerLocation.pathname === ACCOUNT_ROUTES.orders;

	return (
		<div className={cn(className, css.wrapper)}>
			<CdnImage
				path={`https://cdn.websky.aero/content/frontend/images/aircraftImages/${iataCode.toUpperCase()}/default.png`}
				fallbackUrl=""
				className={css.img}
				isBackground
			/>
			<div className={css.actions}>
				<BaseButton variant="smallNeitral" onClick={openSearchFlights}>
					{t('Search flights')}
				</BaseButton>
				<BaseButton variant="smallNeitral" onClick={onSetAddBookingModalType(FormType.Booking)}>
					{t('I have a booking')}
				</BaseButton>
				<BaseButton variant="smallNeitral" onClick={onSetAddBookingModalType(FormType.Checkin)}>
					{t('Check-in')}
				</BaseButton>
				{!isOrdersPage && (
					<BaseButton variant={'smallNeitral'} onClick={goToOrders}>
						{t('My orders')}
					</BaseButton>
				)}

				{additionalButtons}
			</div>
			<ModalConfirm
				size="medium"
				isOpen={!!addBookingModalType}
				onClose={onClearAddBookingModalType}
				noPadding={true}
			>
				<AddBooking
					order={order}
					isLoading={isLoading}
					type={addBookingModalType || lastModalTypeRef.current}
					onClose={onClearAddBookingModalType}
					onSubmit={addBookingModalType === FormType.Booking ? onBookingSubmit : onCheckinSubmit}
					onClearOrder={onClearOrder}
					onViewOrderClick={onViewOrderClick}
				/>
			</ModalConfirm>
		</div>
	);
};

export default ProfileControls;
